import React, { useMemo, useRef } from "react";
import { toast } from "sonner";
import { Badge } from "../../components/ui/badge";
import ResourceManager from "../../components/admin/ResourceManager";
import { fmtDate } from "../../lib/resources";

const STD_CATEGORIES = ["Robotics", "Electronics", "Books", "Merch"];

export default function ManageProducts() {
  const prevCategoryRef = useRef("");

  const dynamicFields = useMemo(() => (fields, form) => {
    const cat = form?.category || "";
    const isOther = cat === "Other";
    const isCustom = Boolean(cat && !STD_CATEGORIES.includes(cat) && cat !== "Other");

    const result = [];
    for (const f of fields) {
      if (f.key === "category") {
        const opts = [
          ...STD_CATEGORIES.map((v) => ({ value: v, label: v })),
          { value: "Other", label: "Other" },
        ];
        if (isCustom && !opts.find((o) => o.value === cat)) {
          opts.push({ value: cat, label: cat });
        }
        result.push({ ...f, options: opts });
        if (isOther) {
          result.push({
            key: "custom_category",
            label: "Enter Custom Category",
            type: "text",
            placeholder: "e.g. Science Kit",
          });
        }
      } else {
        result.push(f);
      }
    }
    return result;
  }, []);

  const onFormChange = (form, setForm) => {
    const cat = form?.category || "";
    if (prevCategoryRef.current !== cat && prevCategoryRef.current === "Other") {
      setForm?.((s) => ({ ...s, custom_category: "" }));
    }
    prevCategoryRef.current = cat;
  };

  const onBeforeSave = (form) => {
    if (form.category === "Other" && !form.custom_category?.trim()) {
      toast.error("Please enter category name.");
      return false;
    }
  };

  const transformBeforeSave = (form) => {
    const body = { ...form };
    if (body.category === "Other") {
      body.category = body.custom_category?.trim() || "";
    }
    delete body.custom_category;
    return body;
  };

  return (
    <ResourceManager
      kind="products"
      selectable={true}
      eyebrow="Store"
      title="Products"
      subtitle="Kits, bundles and merchandise sold in the student Shop."
      excelHint="Columns: name, description, price, stock, category, sku"
      fields={[
        { key: "name", label: "Product Name", type: "text", required: true },
        { key: "category", label: "Category", type: "select", placeholder: "Pick category",
          options: [
            { value: "Robotics", label: "Robotics" },
            { value: "Electronics", label: "Electronics" },
            { value: "Books", label: "Books" },
            { value: "Merch", label: "Merch" },
            { value: "Other", label: "Other" },
          ] },
        { key: "sku", label: "SKU", type: "text", placeholder: "e.g. RBT-101" },
        { key: "price", label: "Price (₹)", type: "number", required: true },
        { key: "stock", label: "Stock", type: "select", options: [
          { value: "in", label: "In stock" },
          { value: "low", label: "Low" },
          { value: "out", label: "Out of stock" },
        ] },
        { key: "image", label: "Product Image", type: "image" },
        { key: "images", label: "Product Gallery Images", type: "images", span: 2 },
        { key: "description", label: "Description", type: "textarea", span: 2 },
      ]}
      columns={[
        { key: "image", label: "Image", render: (r) => {
          const allImgs = (Array.isArray(r.images) ? r.images : []).filter(Boolean);
          const firstImg = allImgs[0] || (typeof r.image === "string" ? r.image : r.image?.url);
          return firstImg ? <img src={firstImg} alt="" loading="lazy" className="w-12 h-12 rounded-lg object-cover border border-white/10" /> : <span className="text-[#64748B]">—</span>;
        }},
        { key: "name", label: "Product" },
        { key: "category", label: "Category" },
        { key: "sku", label: "SKU" },
        { key: "price", label: "Price", render: (r) => <span className="text-cyan-200">₹ {r.price ?? 0}</span> },
        { key: "stock", label: "Stock", render: (r) => (
          <Badge variant="outline" className={r.stock === "out" ? "border-red-400/40 text-red-300 bg-red-500/10" : r.stock === "low" ? "border-amber-400/40 text-amber-200 bg-amber-500/10" : "border-emerald-400/40 text-emerald-300 bg-emerald-500/10"}>
            {r.stock || "in"}
          </Badge>
        )},
        { key: "created_at", label: "Added", render: (r) => fmtDate(r.created_at) },
      ]}
      downloadable
      viewable
      filters={[
        { key: "category", label: "Category", options: ["Robotics","Electronics","Books","Merch","Other"].map((v) => ({ value: v, label: v })) },
        { key: "stock", label: "Stock", options: [{ value:"in", label:"In stock" },{ value:"low", label:"Low" },{ value:"out", label:"Out of stock" }] },
      ]}
      dynamicFields={dynamicFields}
      onFormChange={onFormChange}
      onBeforeSave={onBeforeSave}
      transformBeforeSave={transformBeforeSave}
    />
  );
}
